import React from 'react';

import get from 'lodash/get';

import { Grid, HelpOutlineIcon } from '@gogame/components';
import {
  FormField,
  useSiblingFieldName,
  withFormFieldGroupFormField,
} from '@gogame/form';

import {
  CONDITIONS,
  METRIC,
  OPERATOR,
  PERIODS_COMPARE,
} from 'scenes/SmartRules/SmartRuleForm/Conditions';
import {
  loadComparePeriods,
  loadPayTo,
  loadPeriods,
  loadPeriodTypes,
} from 'scenes/SmartRules/sources/fields';

import { useExtraContext } from '../ExtraContext';
import {
  COMPARE_PERIOD,
  DATA_PERIOD,
  PAY_TO,
  PERIOD_TYPE,
  PERIODS,
  PERIODS_LAST_DAY,
  TODAY,
} from './constants';
import { periodsValidationSchema } from './schemas';

const LIFETIME_PERIOD_IF = 'Lifetime';

const getOptionInfoDescription = ({ description }) =>
  description ? { hint: description } : null;

const getOptionIconInfoDescription = ({ description }) =>
  description ? <HelpOutlineIcon sx={{ fontSize: 16 }} /> : null;

const normalizeValueSetFirstOption = (_value, data) => data?.[0]?.id;

export const Periods = withFormFieldGroupFormField({
  name: PERIODS,
  validationSchema: periodsValidationSchema,
})(({ formik, dirty, value, name }) => {
  const getSiblingFieldName = useSiblingFieldName();
  const { hasDynamicConditions } = useExtraContext();
  const { [DATA_PERIOD]: dataPeriodId, [PERIOD_TYPE]: periodType } =
    value || {};

  const { [DATA_PERIOD]: dataPeriod } = get(formik.hierarchyState, name) || {};

  const { isDynamicDataPeriod, isShowPayTo, isTodayDefaultPayToValue } =
    React.useMemo(
      () => dataPeriod?.data?.find(({ id }) => id === dataPeriodId) || {},
      [dataPeriodId, dataPeriod]
    );

  const { [CONDITIONS]: conditions } = formik.values;

  const comparePeriodsShow = React.useMemo(
    () =>
      conditions?.some(({ [OPERATOR]: operatorId }, i) => {
        if (!operatorId) {
          return false;
        }
        const { [OPERATOR]: operator } =
          get(formik.hierarchyState, `${CONDITIONS}.${i}`) || {};
        const type = operator?.data?.find(({ id }) => id === operatorId)?.type;
        return type === PERIODS_COMPARE;
      }),
    [conditions, formik.hierarchyState]
  );

  React.useEffect(() => {
    formik.setFieldValue(
      getSiblingFieldName(PAY_TO),
      isTodayDefaultPayToValue ? TODAY : PERIODS_LAST_DAY
    );
  }, [isTodayDefaultPayToValue]);

  const validateDataPeriod = React.useCallback(() => {
    if (hasDynamicConditions && !isDynamicDataPeriod) {
      return 'Selected data period is not supported for Dynamic Condition';
    }
  }, [hasDynamicConditions, isDynamicDataPeriod]);

  const isAllowPeriodType = React.useMemo(() => {
    return conditions?.every(({ [METRIC]: metricId }, i) => {
      if (!metricId) {
        return false;
      }
      const { [METRIC]: metric } =
        get(formik.hierarchyState, `${CONDITIONS}.${i}`) || {};
      const metricObject = metric?.data?.find(({ id }) => id === metricId);
      return metricObject?.isCohortMetric;
    });
  }, [conditions, formik.hierarchyState]);

  React.useEffect(() => {
    if (dirty && !isAllowPeriodType && periodType) {
      formik.setFieldValue(getSiblingFieldName(PERIOD_TYPE), undefined);
    }
  }, [conditions, isAllowPeriodType, periodType, dirty]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <FormField.Select
          label={'Data period'}
          name={DATA_PERIOD}
          source={loadPeriods}
          lazy={true}
          padding={'0'}
          validate={validateDataPeriod}
          getOptionInfo={getOptionInfoDescription}
          getOptionIconInfo={getOptionIconInfoDescription}
        />
      </Grid>
      {isShowPayTo ? (
        <Grid item xs={4}>
          <FormField.Select
            label={'Pay to'}
            name={PAY_TO}
            source={loadPayTo}
            depends={[getSiblingFieldName(DATA_PERIOD)]}
            lazy={true}
            padding={'0'}
            fi
          />
        </Grid>
      ) : null}
      {comparePeriodsShow && dataPeriodId !== LIFETIME_PERIOD_IF ? (
        <Grid item xs={4}>
          <FormField.Select
            label={'Compare to period'}
            name={COMPARE_PERIOD}
            source={loadComparePeriods}
            depends={[getSiblingFieldName(DATA_PERIOD)]}
            _cleanOnUnmount={true}
            padding={'0'}
            normalizeValue={normalizeValueSetFirstOption}
          />
        </Grid>
      ) : null}
      {isAllowPeriodType ? (
        <Grid item xs={4}>
          <FormField.Select
            label={'Period Type'}
            name={PERIOD_TYPE}
            source={loadPeriodTypes}
            lazy={true}
            padding={'0'}
          />
        </Grid>
      ) : null}
    </Grid>
  );
});
